import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import styled from 'styled-components';
import { sendGoogleAnalyticsEvents } from '@elfsight/lib-google-analytics';
import { Slider } from '@elfsight/lib-toolkit-slider';
import { Show } from '@elfsight/lib-utils';
import { Deactivation2 } from '@elfsight/widget-deactivation';
import { EmbedRoot } from '@elfsight/widget-ui';
import { useBreakpoints } from '@elfsight/widget-utils';
import { useSettings } from '@wu/widget-core';
import { shuffle } from 'es-toolkit';
import { APP_CLASSNAME_PREFIX, MOBILE_BREAKPOINT } from '../constants';
import { meta } from '../meta';
import { Arrow } from './components/arrow/Arrow';
import { FreeLink } from './components/main';
import { Thumbnails } from './components/paginations/Thumbnails';
import {
  LegacySlide,
  slideVariations
} from './components/slides/slide-variations';
import {
  getItemMedia,
  getSlideIndex,
  isSlideAutoHeight,
  useDebounce
} from './helpers';

export function Widget({
  customCSSPrefixClassName
}: {
  customCSSPrefixClassName: string;
}) {
  const settings = useSettings();
  const {
    googleAnalytics,
    editMode,
    height,
    mobileHeight,
    borderRadius,
    arrowsPosition,
    paginationStyle,
    infiniteLoop,
    transitionEffect,
    swipeEnabled,
    mobileSwipeEnabled,
    autoSlideEnabled,
    displayDeactivation,
    autoSlideDelay,
    autoSlidePauseOnHover,
    autoSlideProgressbar,
    arrowsEnabled,
    mobileArrowsEnabled,
    transitionDuration,
    paginationEnabled,
    mobilePaginationEnabled,
    template,
    widgetId,
    slides,
    slideOrder,
    width,
    stretchBackground,
    navigationColor,
    editingItems
  } = settings;

  const slider = useRef(null);
  const currentItemPosition = useRef(0);
  const [isDrag, toggleDrag] = useState(false);
  const [images, setImages] = useState([]);
  const [{ id: currentItemId }, setActiveSlide] = useState(slides?.[0] ?? []);
  const [paginationHeight, setPaginationHeight] = useState(0);

  const [breakpoints, ref] = useBreakpoints(MOBILE_BREAKPOINT);
  const isMobile = breakpoints[MOBILE_BREAKPOINT];
  const sliderWidth = ref.current?.clientWidth;
  const isAutoSlideEnabled = autoSlideEnabled && !displayDeactivation;

  const [isInitialized, setIsInitialized] = useState(false);

  const prevVisibleSlides = useRef(slides);
  const prevSlideOrder = useRef(slideOrder);

  const prevSlides = useRef(slides);

  const finalArrowsEnabled = isMobile
    ? arrowsEnabled && mobileArrowsEnabled
    : arrowsEnabled;
  const finalPaginationEnabled = isMobile
    ? paginationEnabled && mobilePaginationEnabled
    : paginationEnabled;

  const derivedHeight = useDebounce(isMobile ? mobileHeight : height);
  const maxSlideHeight = derivedHeight?.auto
    ? undefined
    : derivedHeight?.customValue;

  useEffect(() => {
    if (!template && widgetId) {
      const hasUpdated =
        JSON.stringify(slides) !== JSON.stringify(prevSlides.current);

      if (hasUpdated && slider.current && slides.length) {
        slider.current.goTo(0);
        prevSlides.current = slides;
      }
    }
  }, [slides, template, widgetId]);

  const [visibleSlides, onlyAutoHeightSlides] = useMemo(() => {
    let onlyAutoHeightSlides = true;

    let sortedSlides = slides.filter((slide) => {
      const { visible, type, image, video, background, elements } = slide;

      if (visible === false) {
        return false;
      }

      onlyAutoHeightSlides = onlyAutoHeightSlides && isSlideAutoHeight(slide);

      if (type === 'image-slide' && !image) {
        return false;
      }

      if (type === 'video-slide' && !video) {
        return false;
      }

      if (type === 'slide-builder') {
        const hasBackground =
          background.type === 'gradient' ||
          (background.type === 'color' && background.color !== 'transparent') ||
          (background.type === 'image' && background.imageFile) ||
          (background.type === 'video' && background.videoUrl);

        if (!hasBackground && !elements.length) {
          return false;
        }
      }

      return true;
    });

    if (slideOrder === 'random') {
      let shuffledArray;
      if (
        prevVisibleSlides.current.length === sortedSlides.length &&
        prevSlideOrder.current === slideOrder &&
        editMode
      ) {
        shuffledArray = prevVisibleSlides.current.map((item) =>
          sortedSlides.find((elem) => elem.id === item.id)
        );
      } else {
        shuffledArray = shuffle(sortedSlides);
      }
      sortedSlides = shuffledArray;
    }

    prevVisibleSlides.current = sortedSlides;
    prevSlideOrder.current = slideOrder;

    return [prevVisibleSlides.current, onlyAutoHeightSlides];
    // oxlint-disable-next-line exhaustive-deps
  }, [slides, slideOrder]);

  useEffect(() => {
    if (visibleSlides.length > 0) {
      const images = visibleSlides.map((slide) => getItemMedia(slide));
      setImages(images);
    }
  }, [visibleSlides]);

  useLayoutEffect(() => {
    const { current: sliderElement } = slider;

    let targetedSlideIndex;
    const targetedSlide =
      sliderElement &&
      visibleSlides.find((slide, index) => {
        if (editMode && slide.id === editingItems['slide']) {
          targetedSlideIndex = index;
          return true;
        }

        return false;
      });

    const isTargetedSlideAvailable =
      targetedSlide && targetedSlide.id !== currentItemId;

    if (isTargetedSlideAvailable) {
      sliderElement.goTo(targetedSlideIndex);
    }
    // oxlint-disable-next-line exhaustive-deps
  }, [visibleSlides, slider, slideOrder, editingItems]);

  useLayoutEffect(() => {
    const { current: sliderElement } = slider;
    if (sliderElement) {
      setActiveSlide(visibleSlides[0]);
      sliderElement.goTo(0);
      setIsInitialized(true);
    }
    // oxlint-disable-next-line exhaustive-deps
  }, [slideOrder]);

  const sliderViewedSentRef = useRef(false);
  const onTranslateEnd = useCallback(() => {
    const {
      current: {
        state: { realIndex }
      }
    } = slider;

    if (visibleSlides[realIndex]) {
      currentItemPosition.current = realIndex;
      setActiveSlide(visibleSlides[realIndex]);
    }

    requestAnimationFrame(() => {
      toggleDrag(false);

      if (sliderViewedSentRef.current) {
        return;
      }

      sliderViewedSentRef.current = true;
      sendGoogleAnalyticsEvents({
        eventType: 'sliderViewed',
        googleAnalyticsEvents: googleAnalytics,
        valueMapping: {
          widgetId
        }
      });
    });
    // oxlint-disable-next-line exhaustive-deps
  }, [slider, visibleSlides, toggleDrag, googleAnalytics, widgetId]);

  const onTranslateStart = useCallback(() => {
    const {
      current: {
        state: { horizontalMove }
      }
    } = slider;

    if (horizontalMove) {
      toggleDrag(true);
    }
    // oxlint-disable-next-line exhaustive-deps
  }, [slider, toggleDrag]);

  const CustomPaginationObject = useMemo(
    () =>
      ({
        thumbnails: Thumbnails,
        bullets: null,
        lines: null,
        numbers: null
      })[paginationStyle],
    // oxlint-disable-next-line exhaustive-deps
    [paginationStyle, Thumbnails]
  );

  const sliderSettings = useMemo(
    () => ({
      slidesPerView: 1,
      speed: transitionDuration,
      ref: slider,
      height: derivedHeight,
      customIcons: {
        prev: <Arrow rotate />,
        next: <Arrow />
      },
      navigationClassName: `${APP_CLASSNAME_PREFIX}-slider-navigation`,
      paginationClassName: `${APP_CLASSNAME_PREFIX}-slider-pagination`,
      autoplayDelay:
        isAutoSlideEnabled && autoSlideDelay > 0 && !editingItems?.slide
          ? autoSlideDelay
          : 0,
      customPagination: CustomPaginationObject
        ? (props) => <CustomPaginationObject {...props} images={images} />
        : null,
      arrows: finalArrowsEnabled,
      paginationType: finalPaginationEnabled ? paginationStyle : 'none',
      loop: infiniteLoop,
      animationType: transitionEffect,
      drag: swipeEnabled,
      mobileDrag: mobileSwipeEnabled,
      colors: {
        arrows: {
          background: 'transparent',
          hover: {}
        },
        pagination: {
          color: navigationColor,
          active: navigationColor
        }
      },
      borderRadius,
      autoplayPauseOnHover: autoSlidePauseOnHover,
      autoplayProgressBar: autoSlideProgressbar,
      onTranslateEnd,
      onTranslateStart,
      arrowsPosition,
      shouldGoToFirstSlideWhenSlidesChange: true,
      shouldUseCombinedNavigation: true,
      onCombinedNavigationPaginationHeightChange: setPaginationHeight,
      appClassnamePrefix: APP_CLASSNAME_PREFIX
    }),
    // oxlint-disable-next-line exhaustive-deps
    [settings, images, currentItemId, isMobile, derivedHeight]
  );

  const parsedWidth = useMemo(() => {
    if (stretchBackground) {
      return '100%';
    }

    return `${width}px`;
  }, [width, stretchBackground]);

  const areArrowsVisible = finalArrowsEnabled && visibleSlides.length > 1;
  const isPaginationVisible =
    finalPaginationEnabled && visibleSlides.length > 1;

  const makeSlideAnalyticsPayload = useCallback(
    ({ id, contentTitleText }) => {
      return {
        widgetId,
        slideID: id,
        slideTitle: contentTitleText,
        slideIndex: getSlideIndex(slides, id)
      };
    },
    [widgetId, slides]
  );

  useEffect(() => {
    if (!isInitialized || !currentItemId || editMode) {
      return;
    }

    const { contentTitleText } =
      slides.find(({ id }) => id === currentItemId) ?? {};
    const payload = makeSlideAnalyticsPayload({
      id: currentItemId,
      contentTitleText
    });

    sendGoogleAnalyticsEvents({
      eventType: 'slideViewed',
      googleAnalyticsEvents: googleAnalytics,
      valueMapping: payload
    });
  }, [
    isInitialized,
    currentItemId,
    googleAnalytics,
    editMode,
    slides,
    makeSlideAnalyticsPayload
  ]);

  return (
    <StyledEmbedRoot
      ref={ref}
      customCSSClassName={customCSSPrefixClassName}
      width={parsedWidth}
    >
      <Show when={visibleSlides.length > 0 && !!sliderSettings}>
        <Deactivation2 appAlias={meta.appAlias} {...settings}>
          <InnerWrapper>
            <Slider {...sliderSettings}>
              {visibleSlides.map((slide) => {
                const Slide = slideVariations[slide.type] ?? LegacySlide;
                const { id, contentTitleText } = slide;
                const payload = makeSlideAnalyticsPayload({
                  id,
                  contentTitleText
                });

                const sendSlideLinkClickEvent = (linkURL) => {
                  sendGoogleAnalyticsEvents({
                    googleAnalyticsEvents: googleAnalytics,
                    eventType: 'slideLinkOpened',
                    valueMapping: {
                      ...payload,
                      linkURL
                    }
                  });
                };

                return (
                  <Slide
                    key={slide.id}
                    data={slide}
                    onSlideLinkClick={sendSlideLinkClickEvent}
                    googleAnalyticsExtraParameters={payload}
                    active={currentItemId === slide.id}
                    isDrag={isDrag}
                    isMobile={isMobile}
                    sliderWidth={sliderWidth}
                    maxSlideHeight={maxSlideHeight}
                    paginationHeight={paginationHeight}
                    navigationElementsVisibility={{
                      arrows: areArrowsVisible,
                      pagination: isPaginationVisible
                    }}
                    width={width}
                    shouldForceMinHeight={
                      onlyAutoHeightSlides && derivedHeight?.auto
                    }
                  />
                );
              })}
            </Slider>
            <FreeLink />
          </InnerWrapper>
        </Deactivation2>
      </Show>
    </StyledEmbedRoot>
  );
}

const StyledEmbedRoot = styled(EmbedRoot).attrs({
  className: `${APP_CLASSNAME_PREFIX}-slider-component`
})`
  && div:empty {
    display: block;
  }
`;

const InnerWrapper = styled.div.attrs({
  className: `${APP_CLASSNAME_PREFIX}-slider-inner-wrapper`
})`
  max-width: 100%;
  will-change: transform;

  & .${APP_CLASSNAME_PREFIX}-slider-slider {
    position: relative;
    overflow: visible;
  }

  & .${APP_CLASSNAME_PREFIX}-slider-pagination {
    padding: 6px 16px;

    .${APP_CLASSNAME_PREFIX}-bullets-dot {
      box-sizing: border-box;
    }

    .${APP_CLASSNAME_PREFIX}-slider-pagination-item-active {
      transform: scale(1.25);
    }
  }
`;
